import { JetView } from 'webix-jet'; // Importing JetView from webix-jet framework
import FilterConsultationDocuments from 'views/betShopManagement/consultationDocuments/filterConsultationDocuments'; // Importing filter component
import TableConsultationDocuments from 'views/betShopManagement/consultationDocuments/tableConsultationDocuments'; // Importing table component
import { devConfig } from 'config/config'; // Importing development configuration

/**
 * Class representing the Consultation Documents view (points of sale, dealers and admin).
 * @extends JetView
 */
export default class ConsultationDocumentsView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const theme = this.app.config.theme; // Current theme configuration

        // Rows of the current page, used by both exports
        const currentPageFilter = () => {
            const table = $$('consultationDocumentsTable'); // Reference to the datatable
            const size = table.getPager().config.size; // Current page size
            const first = size * table.getPage(); // First item index on current page
            const last = first + size - 1; // Last item index on current page
            return function (obj) {
                const index = table.getIndexById(obj.id); // Position of the row in the datatable
                return index >= first && index <= last;
            };
        };

        // Export file name, same format as the other reports
        const exportFileName = () =>
            'Virtualsoft - ' +
            new Date()
                .toLocaleString('en-GB', {
                    hour12: true,
                })
                .replace(/^(\d{4})-(\d{2})-(\d{2})$/g, '$3/$2/$1')
                .replace(/[\/:]/g, '_')
                .toUpperCase() +
            ' - ' +
            ($$('consultationDocumentsTable').count() > 0 ? '' : _('No hay datos - ')) +
            _('Documentos');

        // Toolbar configuration: page size, pager and export
        const toolbar = {
            view: 'toolbar',
            id: 'consultationDocumentsToolbar', // Unique identifier for the toolbar
            css: 'highlighted_header header6', // CSS classes for styling
            paddingX: 5,
            paddingY: 5,
            height: 40,
            cols: [
                {
                    view: 'select', // Dropdown for selecting page size
                    options: ['10', '20', '50', '100'], // Options for page size
                    width: 70, // Dropdown width
                    on: {
                        onChange: function () {
                            $$('consultationDocumentsTable').getPager().config.size = this.getValue() * 1; // Update pager size
                            $$('consultationDocumentsTable').refresh(); // Refresh the datatable
                        },
                    },
                },
                {
                    view: 'pager', // Pager for navigation
                    id: 'consultationDocumentsPager', // Unique identifier for the pager
                    template:
                        '{common.first()}{common.prev()}&nbsp; {common.pages()}&nbsp; {common.next()}{common.last()}',
                    autosize: false,
                    height: 35,
                    size: devConfig.getDefaultDataFetch(), // Default data fetch size
                },
                {
                    view: 'richselect', // Dropdown for export options
                    id: 'consultationDocumentsExport', // Unique identifier for the export dropdown
                    width: 120, // Dropdown width
                    placeholder: _('Exportar'), // Placeholder text
                    options: [
                        { id: '1', value: 'Excel' }, // Excel export option
                        { id: '2', value: 'PDF' }, // PDF export option
                    ],
                    on: {
                        onChange: function (value) {
                            // Export to Excel
                            if (value == 1) {
                                webix.toExcel($$('consultationDocumentsTable'), {
                                    filter: currentPageFilter(), // Only the rows of the current page
                                    filename: exportFileName(), // Filename for export
                                    ignore: { rating: true }, // Ignore actions column
                                    footer: false, // Do not include footer
                                });
                            } else if (value == 2) {
                                // Export to PDF
                                webix.toPDF($$('consultationDocumentsTable'), {
                                    filter: currentPageFilter(), // Only the rows of the current page
                                    filename: exportFileName(), // Filename for export
                                    autowidth: true, // Auto width for PDF
                                    ignore: { rating: true }, // Ignore actions column
                                    footer: false, // Do not include footer
                                });
                            }
                            if (value) {
                                this.setValue(''); // Reset export selection
                            }
                        },
                    },
                },
            ],
        };

        // Main panel configuration
        const main = {
            rows: [
                {
                    view: 'toolbar', // Header toolbar
                    css: theme, // Apply current theme
                    elements: [
                        { view: 'label', label: _('Documentos') }, // Title label
                        {
                            type: 'wide',
                            css: 'close-bottom',
                            id: 'consultationDocumentsSearch', // Search button, only on small screens
                            cols: [
                                {
                                    view: 'icon',
                                    icon: 'wxi-search', // Search icon
                                    width: 30,
                                    height: 30,
                                    click: () => {
                                        $$('consultationDocumentsFilterPopup').show(); // Show filter popup
                                    },
                                },
                            ],
                        },
                        {
                            type: 'wide',
                            css: 'close-bottom',
                            cols: [
                                {
                                    view: 'icon',
                                    icon: 'wxi-close', // Close icon
                                    width: 30,
                                    height: 30,
                                    click: () => this.show('/top/dashboard'), // Navigate to dashboard
                                },
                            ],
                        },
                    ],
                },
                FilterConsultationDocuments, // Include filter component
                TableConsultationDocuments, // Include table component
                toolbar, // Include toolbar
            ],
        };

        webix.ui.fullScreen(); // Set the UI to full screen mode
        return {
            id: 'consultationDocumentsLayout', // Unique identifier for the main layout
            type: 'clean',
            view: 'form', // Main view type
            scroll: 'y', // Enable vertical scrolling
            rows: [
                {
                    type: 'wide', // Same wrapper as the other sections, the app.css title centering depends on it
                    cols: [main],
                },
            ],
        };
    }

    /**
     * Initializes the view and adapts it to small screens.
     */
    init() {
        webix.ready(function () {
            if (screen.width < 500 || screen.height < 500) {
                $$('consultationDocumentsExport').hide(); // No export on small screens, same as the other reports
                $$('consultationDocumentsPager').define('group', 1); // Define pager group for small screens
            } else {
                $$('consultationDocumentsSearch').hide(); // Filters are shown in the accordion on larger screens
            }
        });
    }
}

// FILE DOCUMENTED
